import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { BellRing, X, Clock, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

// Corner pop-up for Important follow-ups when their due date & time arrives (polls every 30s)
export default function FollowupReminders() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const seen = useRef(new Set());
  const load = () => api.get("/followups/reminders").then(({ data }) => {
    setItems(data);
    data.forEach((f) => {
      if (seen.current.has(f.id)) return;
      seen.current.add(f.id);
      try { new Audio("data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=").play().catch(() => {}); } catch (e) { /* no audio */ }
      if (window.Notification?.permission === "granted") new window.Notification("Important follow-up due", { body: `${f.student_name || "Lead"} · ${f.type}` });
    });
  }).catch(() => {});
  useEffect(() => {
    load();
    if (window.Notification?.permission === "default") window.Notification.requestPermission().catch(() => {});
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line
  const act = async (f, body) => { await api.post(`/followups/${f.id}/reminder`, body); setItems((x) => x.filter((i) => i.id !== f.id)); };
  const done = async (f) => { await api.patch(`/followups/${f.id}`, { status: "Completed" }); toast.success("Follow-up marked done"); setItems((x) => x.filter((i) => i.id !== f.id)); };
  if (!items.length) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[60] w-[340px] max-w-[calc(100vw-2rem)] space-y-2" data-testid="fu-reminders">
      {items.map((f) => (
        <div key={f.id} className="bg-white rounded-xl shadow-2xl border-l-4 border-amber-400 overflow-hidden animate-in slide-in-from-right" data-testid={`fu-reminder-${f.id}`}>
          <div className="bg-gradient-to-r from-amber-400 to-orange-500 text-white px-3 py-2 flex items-center gap-2">
            <BellRing className="w-4 h-4 animate-pulse" /><span className="text-xs font-bold uppercase tracking-wider flex-1">Important follow-up due</span>
            <button onClick={() => act(f, { action: "dismiss" })} data-testid={`fu-reminder-dismiss-${f.id}`}><X className="w-4 h-4" /></button>
          </div>
          <div className="p-3">
            <div className="font-heading font-bold text-[#312E81]">{f.student_name || "Follow-up"}</div>
            <div className="text-xs text-slate-500">{f.type} · due {new Date(f.due_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}{f.temp_cover ? ` · covering for ${f.temp_from_name}` : ""}</div>
            {f.notes && <div className="text-sm text-slate-700 mt-1 line-clamp-2">{f.notes}</div>}
            <div className="flex gap-1.5 mt-3">
              {(f.student_id || f.prospect_id) && <button onClick={() => { navigate(f.student_id ? `/students/${f.student_id}` : "/calling-queue"); }} className="flex-1 h-8 rounded-lg bg-[#4F46E5] text-white text-xs font-semibold flex items-center justify-center gap-1" data-testid={`fu-reminder-open-${f.id}`}><ExternalLink className="w-3.5 h-3.5" /> Open</button>}
              <button onClick={() => act(f, { action: "snooze", minutes: 10 })} className="flex-1 h-8 rounded-lg bg-amber-50 text-amber-700 text-xs font-semibold flex items-center justify-center gap-1" data-testid={`fu-reminder-snooze-${f.id}`}><Clock className="w-3.5 h-3.5" /> Snooze 10m</button>
              <button onClick={() => done(f)} className="flex-1 h-8 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-semibold flex items-center justify-center gap-1" data-testid={`fu-reminder-done-${f.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Done</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
